import React, { FC, useEffect } from 'react';
import { MerchantDetail } from '../../../../../interfaces/merchants';
import { ModelType } from '../../../../../interfaces/merchants/resources/books';
import {
  init,
  useWindowPopstate,
} from '../../../../../redux/actions/merchants/resources/books';
import AdsBottom from '../../../../../components/adsense/AdsBottom';
import AdsMiddle1 from '../../../../../components/adsense/AdsMiddle1';
import BaseLayoutMerchantWithShortHeader from '../../../../../components/layouts/BaseLayoutMerchantWithShortHeader';
import BookingErrorPage from '../../../../../organisms/merchants/resources/book/BookingErrorPage';
import ModelTypeCourse from '../../../../../organisms/merchants/resources/book/ModelTypeCourse';
import ModelTypeCourseWithStaff from '../../../../../organisms/merchants/resources/book/ModelTypeCourseWithStaff';
import ModelTypeEvent from '../../../../../organisms/merchants/resources/book/ModelTypeEvent';
import ModelTypeSchool from '../../../../../organisms/merchants/resources/book/ModelTypeScool';

import css from './index.module.scss';
import { TimezoneContext } from '../../../../../contexts/TimezoneContext';
import { useShallowEqualSelector } from '../../../../../hooks/redux';
import { ReduxState } from '../../../../../redux/reducers';
import { getMerchantResourcesBooks } from '../../../../../redux/selectors/merchants/resources/books';
import { useUserMe } from '../../../../../hooks/api/me';
import { LoadingIconWithReloadButton } from '../../../../../molecules/common/LoadingIconWithReloadButton';
import { useAppDispatch } from '../../../../../redux';
import { Types } from 'src/redux/reducers/merchants/resources/books';

interface Props {
  merchantId: string;
  isAccepting: boolean;
  merchantDetail: MerchantDetail;
  resourceId: string;
  selectedDate?: string;
  selectedSlot?: string;
  selectedLesson?: string;
  selectedCourse?: string;
  selectedStaff?: string;
  isCoubicApp: boolean;
}

// MEMO: 旧予約フローのページ(src/pages/[publicId]/[resource_id]/book)
// 事前決済が存在しないM3(レッスンタイプ)/M5(スクールタイプ)のみ利用されるページ
// 新予約フローは以下に移行済み
// src/pages/[publicId]/[resource_id]/book/course_type/[[...slug]]/index.tsx
// src/pages/[publicId]/[resource_id]/book/event_type/[[...slug]]/index.tsx

const MerchantResourceBookPage: FC<Props> = ({
  merchantId,
  resourceId,
  selectedDate,
  selectedSlot,
  selectedLesson,
  selectedCourse,
  selectedStaff,
  isAccepting,
  merchantDetail,
  isCoubicApp,
}) => {
  const dispatch = useAppDispatch();
  const { meResponseData: loginUser } = useUserMe();
  const isEmailVerified = loginUser?.is_email_verified ?? false;

  // 一つ目の予約のページに遷移した後に、別の予約ページに遷移した際に一つ目の予約ページの情報が残っている状態なので、それをクリアする
  useEffect(() => {
    return () => {
      dispatch({ type: Types.Clear });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch(
      init({
        merchantId,
        resourceId,
        selectedDate,
        selectedSlot,
        selectedLesson,
        selectedCourse,
        selectedStaff,
        isEmailVerified,
      }),
    );
  }, [
    merchantId,
    resourceId,
    dispatch,
    selectedDate,
    selectedSlot,
    selectedLesson,
    selectedCourse,
    selectedStaff,
    isEmailVerified,
  ]);

  useWindowPopstate({ dispatch });

  const {
    public_id,
    display_name,
    thumbnail,
    time_zone,
    feature,
    line_liff_id,
    show_profile_image_on_site,
    logo_image,
    on_free_plan,
  } = merchantDetail;

  const merchantResourcesBooksState = useShallowEqualSelector(
    (state: ReduxState) => getMerchantResourcesBooks(state),
  );

  const { settings } = merchantResourcesBooksState;

  const isAvailabilityType =
    isAccepting && settings?.modelType === ModelType.AvailabilityScheme;
  const isEventType =
    isAccepting && settings?.modelType === ModelType.EventScheme;
  const isAssignType =
    isAccepting && settings?.modelType === ModelType.AssignScheme;
  const isSchoolType =
    isAccepting && settings?.modelType === ModelType.SchoolScheme;

  if (!settings) {
    return <LoadingIconWithReloadButton />;
  }

  return (
    <BaseLayoutMerchantWithShortHeader
      className={css.Index}
      merchantProps={{
        displayName: display_name,
        thumbnailUrl: thumbnail.thumb.url,
        isShowProfileImage: show_profile_image_on_site,
      }}
      logoImage={logo_image}
      merchantPublicId={public_id}
      isCoubicApp={isCoubicApp}
      onFreePlan={on_free_plan}
    >
      <TimezoneContext.Provider value={{ timezone: time_zone }}>
        <div className={css.Index__BooksArea}>
          {!feature.is_ad_hiding && !isCoubicApp && (
            <div className={css.Index__AdsContent}>
              <AdsMiddle1 />
            </div>
          )}
          <div className={css.Index__BooksContent}>
            {!isAccepting && <BookingErrorPage withExpressFlow={false} />}
            {isAvailabilityType && (
              <ModelTypeCourse
                merchantId={public_id}
                isExpressFlow={false}
                resourcePublicId={resourceId}
                lineLiffId={line_liff_id}
              />
            )}
            {isEventType && (
              <ModelTypeEvent
                merchantId={public_id}
                isExpressFlow={false}
                resourcePublicId={resourceId}
                lineLiffId={line_liff_id}
              />
            )}
            {isAssignType && (
              <ModelTypeCourseWithStaff
                merchantId={public_id}
                isExpressFlow={false}
                resourcePublicId={resourceId}
                lineLiffId={line_liff_id}
              />
            )}
            {isSchoolType && (
              <ModelTypeSchool
                merchantId={public_id}
                isExpressFlow={false}
                resourcePublicId={resourceId}
                lineLiffId={line_liff_id}
              />
            )}
          </div>
          {!feature.is_ad_hiding && !isCoubicApp && (
            <div className={css.Index__AdsContent}>
              <AdsBottom />
            </div>
          )}
        </div>
      </TimezoneContext.Provider>
    </BaseLayoutMerchantWithShortHeader>
  );
};

export default MerchantResourceBookPage;
